import React, { useEffect } from 'react';
import { Box, Container, Paper, Typography, Divider } from '@mui/material';
import { TokenResponse } from '@react-oauth/google';

import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import { useUser } from '../../context/UserContext';

const TermsPage = () => {
    const { login, isLoading } = useUser();

    // Прокрутка вверх при открытии страницы
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const handleSuccess = async (tokenResponse: TokenResponse) => {
        try {
            await login(tokenResponse);
        } catch (loginError) {
            console.error(loginError);
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                background: (theme) =>
                    `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Header onLoginSuccess={handleSuccess} isLoading={isLoading} />

            <Container component="main" maxWidth="md" sx={{ flexGrow: 1, py: 4 }}>
                <Paper sx={{ p: { xs: 3, md: 5 } }}>
                    <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700, color: 'primary.main' }}>
                        Terms and Conditions
                    </Typography>

                    <Typography variant="body2" color="text.secondary" paragraph>
                        Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                        1. Introduction
                    </Typography>
                    <Typography paragraph>
                        Welcome to Ecom. These Terms and Conditions ("Terms") govern your use of our website located at https://ecom.time2future.com/ and the AI-powered services provided by <strong>ISN Group d.o.o.</strong> ("Company", "we", "us", or "our").
                    </Typography>
                    <Typography paragraph variant="body2" sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1 }}>
                        <strong>Company Information:</strong><br />
                        Name: ISN Group d.o.o.<br />
                        Address: Dunajska cesta 159, 1000 Ljubljana, Slovenia<br />
                        Tax ID: 20841515<br />
                        Contact Email: support@ecom.time2future.com
                    </Typography>
                    <Typography paragraph>
                        By creating an account or purchasing credits, you agree to these Terms. If you do not agree, please do not use our services.
                    </Typography>

                    <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                        2. Description of Service
                    </Typography>
                    <Typography paragraph>
                        Ecom provides AI-powered tools for e-commerce merchants, including market research, content enhancement, infographics generation, and product photography transformation. The services are accessed via a credit-based system.
                    </Typography>

                    <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                        3. Credits and Payments
                    </Typography>
                    <ul>
                        <li>
                            <Typography paragraph>
                                <strong>Credit System:</strong> Our services are paid for using "Credits". You may purchase Credits on a one-time basis. Credits are non-transferable and have no monetary value outside of our platform.
                            </Typography>
                        </li>
                        <li>
                            <Typography paragraph>
                                <strong>Free Trial:</strong> We offer 10 free credits to new users upon registration to test the service. No credit card is required for this trial.
                            </Typography>
                        </li>
                        <li>
                            <Typography paragraph>
                                <strong>Payment Processor:</strong> Our order process is conducted by our online reseller Paddle.com. Paddle.com is the Merchant of Record for all our orders. Paddle provides all customer service inquiries and handles returns.
                            </Typography>
                        </li>
                    </ul>

                    <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                        4. Acceptable Use Policy (AUP)
                    </Typography>
                    <Typography paragraph>
                        You agree not to use Ecom to generate, upload, or share content that:
                    </Typography>
                    <ul>
                        <li>Is illegal, harmful, threatening, abusive, harassing, defamatory, or hateful.</li>
                        <li>Promotes sexual violence, self-harm, or illegal acts.</li>
                        <li>Contains explicit adult content or pornography.</li>
                        <li>Infringes on the intellectual property rights of others.</li>
                        <li>Is intended to deceive or spread misinformation.</li>
                    </ul>
                    <Typography paragraph>
                        We reserve the right to suspend or terminate your account without refund if you violate this policy.
                    </Typography>

                    <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                        5. Intellectual Property & AI Content
                    </Typography>
                    <ul>
                        <li><strong>Your Input:</strong> You retain ownership of the original images and data you upload to the platform.</li>
                        <li><strong>AI Output:</strong> Subject to your compliance with these Terms, you own the rights to the content generated by Ecom for your specific inputs.</li>
                        <li><strong>Platform Rights:</strong> ISN Group d.o.o. retains all rights to the underlying software, algorithms, and interface of Ecom.</li>
                    </ul>

                    <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                        6. Disclaimer of Warranties
                    </Typography>
                    <Typography paragraph>
                        The Service is provided "as is". We use third-party AI models (such as OpenAI, Gemini, Perplexity) to generate content. We cannot guarantee that AI-generated content will be 100% accurate, unique, or free of errors (hallucinations). You are responsible for reviewing all generated content before publishing it on marketplaces.
                    </Typography>

                    <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                        7. Governing Law
                    </Typography>
                    <Typography paragraph>
                        These Terms are governed by the laws of Slovenia. Any disputes arising from these Terms shall be resolved in the courts of Ljubljana, Slovenia.
                    </Typography>

                </Paper>
            </Container>

            <Footer />
        </Box>
    );
};

export default TermsPage;
